import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { SessionExpiredService } from '../services/session-expired.service';

/**
 * HTTP Interceptor to handle MSAL authentication errors
 * Clears corrupted token cache when 401/403 errors occur
 */
export const msalErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const msalService = inject(MsalService);
  const router = inject(Router);
  const sessionExpired = inject(SessionExpiredService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle authentication errors (401) - token invalid/expired, need to re-authenticate
      // DO NOT redirect on 403 - that's authorization failure (insufficient permissions), not authentication failure
      // PM/Finance users will get 403 on admin-only endpoints, which is expected and should NOT trigger redirect
      if (error.status === 401) {
        // Clear potentially corrupted MSAL cache
        try {
          // Clear active account
          msalService.instance.setActiveAccount(null);

          // Clear interaction state
          sessionStorage.removeItem('interaction-in-progress');
          localStorage.removeItem('interaction-in-progress');

          // Clear MSAL cache from localStorage
          const msalKeys: string[] = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('msal.') || key.includes('msal'))) {
              msalKeys.push(key);
            }
          }
          msalKeys.forEach(key => localStorage.removeItem(key));

          // Show a friendly centered "session expired" modal instead of an abrupt redirect.
          // The modal's "Sign in again" button performs the navigation to /login. On the login
          // page itself there's nothing to interrupt, so redirect straight there.
          if (router.url.includes('/login')) {
            router.navigate(['/login']);
          } else {
            sessionExpired.notifyExpired();
          }
        } catch (clearError) {
          console.error('Error clearing MSAL cache:', clearError);
        }
      }
      
      // 403 errors are authorization failures (insufficient permissions)
      // These are expected for PM/Finance users accessing admin-only endpoints
      // Let the component handle these gracefully (return empty arrays)
      // DO NOT redirect - user is authenticated, just doesn't have permission

      // DON'T handle token endpoint errors here - let MSAL handle them
      // Token endpoint errors (400 from login.microsoftonline.com) are handled by MSAL interceptor
      // We only handle API errors (401 from our backend)

      return throwError(() => error);
    })
  );
};

